import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { StateInterface } from 'client/Interfaces/StateInterface'

import { showEstimateEmailModal } from '../actions/email'
import { getDocument } from '../api/document'
import * as api from '../api/draft'
import { getEstimateCount } from '../api/estimate'
import { getNextNumber, InvoicenumberResponseInterface } from '../api/invoiceNumber'
import { Analytics } from '../Global/Analytics'
import {
  AnalyticsObject,
  AnalyticsType,
  Category,
  deliveryType,
  Events,
  Placement,
  Result,
  trackingFields,
} from '../Global/AnalyticsEvents'
import { ConfigInterface } from '../Interfaces/ConfigsInterface'
import { DocumentTypes, DraftInterface } from '../Interfaces/Draft/DraftInterface'
import { SettingsInterface } from '../Interfaces/Settings/SettingsInterface'
import { defaultTypeForDocument, getInvoiceType } from '../Other/Configs'
import { discardExtraTotals, handleFieldsBeforeSave } from '../Other/functions/modelFunctions'
import { Immutable } from '../Other/Immutable'
import { actionCreatorAsync, AsyncAction } from './'
import { DraftActions, EstimateActions } from './actionTypes'
import {
  getDraftForPreview,
  loadVitalsForEditor,
  navigateToEditor,
  updateDraftCompanyFromCompany,
  updateDraftCompanyFromSettings,
  updateDraftCustomerFromCustomers,
  updateEstimateNumber,
} from './draft'
import { downloadEstimatePdfById } from './estimate'
import { setNavigation } from './navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from './ongoing'

const handleFields = (draft: DraftInterface, settings: SettingsInterface) => {
  draft = handleFieldsBeforeSave(draft, settings)
  delete draft.isDirty
  return draft
}

const setTypeForEstimateDraft = (draft: DraftInterface, config: ConfigInterface, country: string) => {
  const documentType = getInvoiceType(defaultTypeForDocument(DocumentTypes.ESTIMATE), config, country)
  draft.fields.type = { name: 'type', value: parseInt(documentType.id, 10) }
  draft.fields.typeName = { name: 'typeName', value: documentType.type }
}

function cleanDraftForApproval(draft: DraftInterface): DraftInterface {
  let cleanDraft = Immutable.copy(draft)
  cleanDraft = discardExtraTotals(cleanDraft)
  delete cleanDraft?.invalidFields
  delete cleanDraft?.preview
  delete cleanDraft?.legacyInvoiceId
  delete cleanDraft?.fields?.referenceFieldsOrder
  return cleanDraft
}

const getCountry = (state: StateInterface) => {
  return state.company ? state.company.country : ''
}

export const createEstimateDraft = (draft: DraftInterface) => {
  if (draft.id === 0) {
    delete draft.id
  }
  return async (dispatch, getState) => {
    const state = getState()
    const { settings, config } = state
    draft = handleFields(draft, settings)
    setTypeForEstimateDraft(draft, config, getCountry(state))
    await dispatch(
      actionCreatorAsync(DraftActions.CREATE_ESTIMATE_DRAFT, async () => {
        const createdId = await api.createDraft(DocumentTypes.ESTIMATE, draft)
        return await api.getDraft(DocumentTypes.ESTIMATE, createdId)
      }),
    )
  }
}

export const updateEstimateDraft = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    draft = handleFields(draft, getState().settings)
    setTypeForEstimateDraft(draft, getState().config, getCountry(getState()))
    await dispatch(
      actionCreatorAsync(DraftActions.UPDATE_ESTIMATE_DRAFT, async () => {
        return await api.updateDraft(DocumentTypes.ESTIMATE, draft)
      }),
    )
  }
}

export function loadEstimateDraft(id: number): AsyncAction<DraftInterface> {
  return actionCreatorAsync<DraftInterface>(DraftActions.LOAD_DRAFT, async () =>
    api.getDraft(DocumentTypes.ESTIMATE, id),
  )
}

export const editDraft = (id: number) => {
  Analytics.trackEvent(`${Events.openDocument}`, {
    type: AnalyticsType.action,
    category: Category.draft,
    result: Result.success,
    placement: Placement.estimates,
    documentType: AnalyticsObject.estimate,
    draftID: id,
  })

  return async (dispatch, getState) => {
    const nextEstimateNumber: InvoicenumberResponseInterface = await getNextNumber(DocumentTypes.ESTIMATE)
    await loadVitalsForEditor(dispatch, getState())
    const { settings, company } = getState()
    await dispatch(loadEstimateDraft(id))
    await dispatch(updateEstimateNumber(nextEstimateNumber))
    dispatch(updateDraftCompanyFromSettings(settings))
    dispatch(updateDraftCompanyFromCompany(company))
    await navigateToEditor(dispatch, getState)
  }
}

export const getDraftPreview = (draft: DraftInterface): AsyncAction<string[]> => {
  return actionCreatorAsync<string[]>(DraftActions.PREVIEW_DRAFT, async () =>
    api.getHtmlPreview(DocumentTypes.ESTIMATE, draft),
  )
}

export const navigateToPreview = () => {
  return async (dispatch, getState) => {
    let origin = null
    if (getState().navigation.path !== '/reactInvoice') {
      origin = getState().navigation.path
    }
    const nav = {
      path: '/EstimatePreview',
      app: {
        mod: 'EstimatePreview',
      },
      origin,
    }
    await dispatch(setNavigation(nav))
  }
}

export const previewDraft = (id?: number) => {
  return async (dispatch, getState) => {
    if (getState().navigation.path !== '/reactInvoice' && id) {
      const nextEstimateNumber: InvoicenumberResponseInterface = await getNextNumber(DocumentTypes.ESTIMATE)
      await loadVitalsForEditor(dispatch, getState())
      await dispatch(loadEstimateDraft(id))
      await dispatch(updateEstimateNumber(nextEstimateNumber))
    }
    let draft = Immutable.copy(getState().draft)
    draft = handleFieldsBeforeSave(draft, getState().settings)
    dispatch(getDraftPreview(getDraftForPreview(draft, getState().settings)))
    dispatch(navigateToPreview())
  }
}

export function loadDraftCopy(id: number): AsyncAction<DraftInterface> {
  return actionCreatorAsync<DraftInterface>(DraftActions.COPY_DRAFT, async () =>
    api.getDraft(DocumentTypes.ESTIMATE, id),
  )
}

export const copyDraft = (id: number) => {
  return async (dispatch, getState) => {
    await dispatch(loadDraftCopy(id))
    const nextEstimateNumber: InvoicenumberResponseInterface = await getNextNumber(DocumentTypes.ESTIMATE)
    const { settings, company, customers } = getState()
    dispatch(updateDraftCompanyFromSettings(settings))
    dispatch(updateDraftCompanyFromCompany(company))
    dispatch(updateDraftCustomerFromCustomers(customers.collection))
    await dispatch(updateEstimateNumber(nextEstimateNumber))
    await navigateToEditor(dispatch, getState)
  }
}

export const approveDraftWithoutSending = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    draft = handleFields(draft, getState().settings)
    const estimateCount = (await getEstimateCount()).total
    await dispatch(saveDraft(draft))
    await dispatch(approveDraft(getState().draft))
    Analytics.trackEvent(`${Events.approveDocument}`, {
      type: AnalyticsType.action,
      category: Category.draft,
      result: Result.success,
      placement: Placement.preview,
      documentType: AnalyticsObject.estimate,
      documentID: getState().estimate.id,
      firstDocument: estimateCount === 0,
      documentCount: estimateCount,
    })
  }
}

export const approveDraftAndDownloadPdf = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    await dispatch(saveDraft(draft))
    await dispatch(approveDraft(getState()?.draft))
    const estimateCount = (await getEstimateCount())?.total
    const { navigation, estimate } = getState()
    Analytics.trackEvent(`${Events.approveDocument}`, {
      type: AnalyticsType.action,
      category: Category.draft,
      result: Result.success,
      placement: Placement.preview,
      documentType: AnalyticsObject.estimate,
      documentId: estimate.id,
      firstDocument: estimateCount === 0,
      documentCount: estimateCount,
    })
    dispatch(downloadEstimatePdfById(navigation?.app?.estimate))
    Analytics.trackEvent(`${Events.sendDocument}`, {
      type: AnalyticsType.action,
      category: Category.document,
      result: Result.success,
      placement: Placement.preview,
      documentType: AnalyticsObject.estimate,
      documentID: estimate.id,
      deliveryType: trackingFields?.deliveryType?.pdf,
    })
  }
}

export const approveDraftAndShowEmailModal = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    await dispatch(saveDraft(draft))
    await dispatch(approveDraft(getState().draft))
    const { estimate } = getState()
    const estimateCount = (await getEstimateCount()).total
    Analytics.trackEvent(`${Events.approveDocument}`, {
      type: AnalyticsType.action,
      category: Category.draft,
      result: Result.success,
      placement: Placement.preview,
      documentType: AnalyticsObject.estimate,
      documentId: estimate.id,
      firstDocument: estimateCount === 0,
      documentCount: estimateCount,
    })
    dispatch(showEstimateEmailModal(getState().estimate.id))
    Analytics.trackEvent(`${Events.sendDocument}`, {
      type: AnalyticsType.action,
      category: Category.document,
      result: Result.open,
      placement: Placement.preview,
      documentType: AnalyticsObject.estimate,
      deliveryType: deliveryType.email,
    })
  }
}

export const saveDraft = (draft: DraftInterface, showSpinner = true) => {
  if (draft.id === 0) {
    delete draft.id
  }
  return async (dispatch, getState) => {
    if (draft.id) {
      await dispatch(updateEstimateDraft(draft))
    } else {
      draft = handleFieldsBeforeSave(draft, getState().settings)
      setTypeForEstimateDraft(draft, getState().config, getCountry(getState()))
      if (showSpinner) {
        dispatch(showGeneratingDocumentSpinner())
      }
      await dispatch(createEstimateDraft(draft))
      if (showSpinner) {
        dispatch(hideGeneratingDocumentSpinner())
      }
    }
  }
}

export const approveDraft = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    const cleanDraft = cleanDraftForApproval(draft)
    dispatch(showGeneratingDocumentSpinner())
    const estimateCount = (await getEstimateCount()).total
    const approved = await api.approveNonInvoiceDraft(draft.document, cleanDraft, estimateCount)

    if (approved.id) {
      Segment.track(SegmentEvents.estimates.add, { quote_id: approved.id })
    }

    await dispatch(
      actionCreatorAsync(EstimateActions.SET_ESTIMATE, async () => {
        return getDocument(DocumentTypes.ESTIMATE, approved.id)
      }),
    )
    const estimateId = getState().estimate.id as number
    dispatch(hideGeneratingDocumentSpinner())
    const path = `/reactEstimateList/estimate/${estimateId}`
    const navigation = {
      path,
      app: {
        mod: 'invoices',
        'tabs-invoices': 'estimates',
        estimate: estimateId,
      },
    }
    await dispatch(setNavigation(navigation))
  }
}
